import React, {useState, useRef, useCallback} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Plus, CaretRight} from '../../icons';
import {useDismissOnOutside} from '../../../hooks/useDismissOnOutside';

/**
 * CTA de la fila del listado que apunta a algo que vive DENTRO de una línea de control.
 *
 * El caso que lo motiva son los objetivos: se definen por línea, así que un botón "Definir
 * objetivos" a nivel cliente no tiene un único destino. Antes caía en el panel del cliente y el
 * usuario tenía que salir a buscar dónde se define lo que le acababan de ofrecer.
 *
 * Resuelve las tres situaciones posibles con la misma pieza:
 * - **una sola línea** → link directo, sin pasos intermedios (el 90% de los clientes);
 * - **varias líneas** → menú "¿En qué línea de control?" y recién ahí navega;
 * - **sin líneas conocidas** → `fallbackPath`. No es sólo el cliente recién creado: el Backend y el
 *   Frontend no deployan a la vez, así que con el payload viejo (sin `business_units`) el botón
 *   tiene que seguir llevando a algún lado razonable en vez de desaparecer o romper.
 *
 * Es genérico a propósito (`buildPath`): cualquier otro CTA de estado vacío que apunte adentro de
 * una línea —alertas de una línea, presupuesto, campañas— se resuelve pasando otro destino.
 *
 * @param {Array<{id: number, name: string}>} lines - líneas activas del cliente
 * @param {string} label - texto del botón
 * @param {Function} buildPath - (line) => path de destino
 * @param {string} fallbackPath - destino cuando no se conocen las líneas
 * @param {string} [menuTitle] - encabezado del menú de selección
 * @param {string} [multiPath] - con varias líneas, navegar acá en vez de abrir el menú. Existe
 *   porque la vista de líneas del Panel 2.1 (`/client/:id/lines?filtro=…`, card gtAL5axI) YA
 *   resuelve el "¿en cuál?" mostrándolas filtradas: elegir en un menú a ciegas es peor que verlas.
 * @param {string} [className] - la clase del botón (default `cv2-ghost-cta`, el de estado vacío).
 * @param {Function|null} [icon] - el ícono del botón (default `Plus`, que es «agregar»). Un CTA que
 *   no agrega nada —«¿El presupuesto está al día?», que lleva a editar la línea— pasa el suyo.
 */
export function ControlLineCta({lines, label, buildPath, fallbackPath, menuTitle, multiPath,
                                   className = 'cv2-ghost-cta', icon: Icon = Plus}) {
    const {t} = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    const close = useCallback(() => setIsOpen(false), []);

    useDismissOnOutside(containerRef, isOpen, close);

    const available = (lines || []).filter((line) => line && line.id);

    // El click no debe disparar además la navegación de la fila (que abre el panel del cliente).
    const stop = (event) => event.stopPropagation();
    const content = (
        <>
            {Icon && <Icon size={12} weight="bold"/>}
            {label}
        </>
    );

    if (!available.length) {
        return (
            <Link className={className} to={fallbackPath} onClick={stop}>
                {content}
            </Link>
        );
    }

    if (available.length === 1) {
        const [line] = available;
        return (
            <Link
                className={className}
                to={buildPath(line)}
                onClick={stop}
                title={t('clients_v2.line_cta_direct_title', {name: line.name, interpolation: {escapeValue: false}})}
            >
                {content}
            </Link>
        );
    }

    if (multiPath) {
        return (
            <Link className={className} to={multiPath} onClick={stop}>
                {content}
            </Link>
        );
    }

    return (
        <div className="cv2-linepick" ref={containerRef} onClick={stop}>
            <button
                type="button"
                className={className}
                aria-haspopup="true"
                aria-expanded={isOpen}
                onClick={() => setIsOpen((open) => !open)}
            >
                {content}
            </button>
            {isOpen && (
                <div className="cv2-linepick__menu">
                    <span className="cv2-linepick__title">
                        {menuTitle || t('clients_v2.line_cta_menu_title')}
                    </span>
                    <div className="cv2-linepick__list">
                        {available.map((line) => (
                            <Link
                                key={line.id}
                                className="cv2-linepick__item"
                                to={buildPath(line)}
                                onClick={close}
                            >
                                <span className="cv2-linepick__name">{line.name}</span>
                                <CaretRight size={12} weight="bold"/>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ControlLineCta;
